import { RuleFilled } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { useProvenanceFromMessage } from 'bones/components/molecules/Voicebox/hooks/useProvenanceFromMessage';
import { VoiceboxPanelDataSources } from 'bones/components/molecules/Voicebox/panel/VoiceboxDataSources';
import { VoiceboxPanel } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanel';
import { VoiceboxPanelAnalysis } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelAnalysis';
import { VoiceboxPanelBreadcrumb } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelBreadcrumb';
import { VoiceboxPanelMessageSummary } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelMessageSummary';
import { VoiceboxPanelQueryExplanation } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelQueryExplanation';
import { VoiceboxPanelReasoning } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelReasoning';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import {
  VoiceboxMessage,
  VoiceboxRequestMessage,
  VoiceboxResponseActionType,
} from 'bones/constants/voicebox/message';
import { Button } from 'bones/design-system/components/Button';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { getSettingsFromRequestMessage } from 'bones/utils/voicebox/getSettingsFromRequestMessage';

export interface VoiceboxMessagePanelProps {
  isAnalysisOpen?: boolean;
  isChartOpen?: boolean;
  isDataSourcesOpen: boolean;
  messageId: string;
  setIsDataSourcesOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const VoiceboxMessagePanel: React.VFC<VoiceboxMessagePanelProps> = ({
  isAnalysisOpen,
  isChartOpen,
  isDataSourcesOpen,
  messageId,
  setIsDataSourcesOpen,
}) => {
  const { config, copy, testIds } = useVoiceboxSchema();
  const { messages } = useVoiceboxConversation();
  const { addPanelHistory, getLabelOrLocalName } = useVoiceboxPanelData();
  const { modelsByDatabaseId } = useVoiceboxSettings();

  const messageIdx = messages.findIndex(
    (message) => message.messageId === messageId
  );
  const prevRequestMessage = messages
    .slice(0, messageIdx)
    .reverse()
    .find((message): message is VoiceboxRequestMessage =>
      Boolean(message.isRequest)
    );

  const settings = prevRequestMessage
    ? getSettingsFromRequestMessage(prevRequestMessage, config.defaultSettings)
    : null;

  const message = messages[messageIdx] as VoiceboxMessage | null;
  const provenance = useProvenanceFromMessage(
    message?.isResponse ? message : null,
    settings
  );

  if (!message?.isResponse || !prevRequestMessage || !settings) {
    return null;
  }

  const reasoningAction = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.REASONING
  );

  const model = modelsByDatabaseId[settings.databaseId]?.[
    settings.model || DEFAULT_MODEL_NAME
  ] as QueryBuilderModel | undefined;

  return (
    <VoiceboxPanel
      key={messageId}
      actions={
        !model || !provenance?.graphs.length ? null : (
          <Button
            active={isDataSourcesOpen}
            leftIcon={RuleFilled}
            onClick={() => setIsDataSourcesOpen((prev) => !prev)}
            size="sm"
            testId={testIds.panel.header.dataSources}
            variant="secondary2-gray"
          >
            {copy.panel.header.dataSources}
          </Button>
        )
      }
      badges={
        !model || !provenance?.types.length
          ? undefined
          : provenance.types
              .filter((typeIri) => model.classesByIri[typeIri])
              .map((typeIri, typeIdx) => ({
                key: typeIri,
                children: getLabelOrLocalName(typeIri),
                colorId: model.classesByIri[typeIri].colorId,
                onClick: () => {
                  addPanelHistory({ iri: typeIri, messageId });
                },
                testId: testIds.panel.header.type(typeIri, typeIdx),
                variant: 'light',
              }))
      }
      breadcrumb={<VoiceboxPanelBreadcrumb />}
      slot={
        <VoiceboxPanelAnalysis
          isOpen={isAnalysisOpen}
          message={message}
          settings={settings}
        />
      }
      title={prevRequestMessage.query}
    >
      {!model || !provenance?.graphs.length ? null : (
        <VoiceboxPanelDataSources
          graphs={provenance.graphs}
          isOpen={isDataSourcesOpen}
          messageId={messageId}
          metadata={provenance.metadata}
          model={model}
        />
      )}
      <VoiceboxPanelMessageSummary
        isChartOpen={isChartOpen}
        message={message}
        messageIdx={messageIdx}
        settings={settings}
      />
      {reasoningAction ? (
        <VoiceboxPanelReasoning
          action={reasoningAction}
          message={message}
          settings={settings}
        />
      ) : null}
      <VoiceboxPanelQueryExplanation message={message} settings={settings} />
    </VoiceboxPanel>
  );
};
